import React from 'react';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import BookingReportSummary, { BOOKING_STATUS_OPTIONS } from '../../components/BookingReportSummary';
import {
  Alert,
  Checkbox,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useAppContext } from '../../services/context/AppContext';
import { doGET } from '../../utils/HttpUtils';
import { getUserCorporateId, isHRUser } from '../../utils/helper';
import {
  downloadUtilisationDashboardPdf,
  fetchUtilisationDashboard,
} from '../../services/utilisationReports';
import {
  UtilisationBreakdown,
  UtilisationBooking,
  UtilisationCouponSummary,
  UtilisationDashboardQuery,
  UtilisationDashboardSnapshot,
} from '../../types/utilisationReports';

type Props = {
  eventId?: string;
};

type CorporateOption = {
  _id: string;
  name?: string;
  companyName?: string;
};

const yyyyMmDd = (value: string) => value.replace(/-/g, '');
const inputDate = (value = new Date()) => [
  value.getFullYear(),
  String(value.getMonth() + 1).padStart(2, '0'),
  String(value.getDate()).padStart(2, '0'),
].join('-');
const displayMonth = (value: string) => {
  if (!/^\d{4}-\d{2}$/.test(value)) return value;
  return new Date(`${value}-01T00:00:00`).toLocaleDateString('en-IN', {
    month: 'short',
    year: '2-digit',
  });
};
const formatNumber = (value: number) => Number(value || 0).toLocaleString('en-IN');
const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const filterLabels: Record<string, string> = {
  dateFrom: 'From', dateTo: 'To', service: 'Service', beneficiary: 'Beneficiary',
  statuses: 'Booking status', paymentStatus: 'Payment status', bookingMode: 'Booking mode',
};
const describeFilters = (query: UtilisationDashboardQuery | null) => Object.entries(query || {})
  .filter(([key, value]) => filterLabels[key] && value && value !== 'all')
  .map(([key, value]) => `${filterLabels[key]}: ${key === 'dateFrom' || key === 'dateTo' ? String(value).replace(/^(\d{4})(\d{2})(\d{2})$/, '$3/$2/$1') : String(value).replace(/_/g, ' ').replace(/,/g, ', ')}`)
  .join(' · ') || 'All bookings';

const chartColours = ['#01999e', '#3f73d8', '#72bf24', '#7757c8', '#d99318', '#93a6aa'];

function breakdownOption(items: UtilisationBreakdown[], label: string) {
  return {
    aria: { enabled: true, description: `${label} breakdown` },
    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
    legend: { bottom: 0, type: 'scroll', textStyle: { color: '#587076' } },
    color: chartColours,
    series: [{
      type: 'pie',
      radius: ['48%', '70%'],
      center: ['50%', '43%'],
      label: { show: false },
      data: items.map((item) => ({ name: item.label, value: item.count })),
    }],
  };
}

function MetricCard({ label, value, detail, colour }: {
  label: string;
  value: string;
  detail: string;
  colour: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, height: '100%', borderRadius: 3 }}>
      <Box sx={{ width: 32, height: 4, bgcolor: colour, borderRadius: 4, mb: 1.5 }} />
      <Typography variant="h4" fontWeight={800} color="#173f46">{value}</Typography>
      <Typography variant="body2" fontWeight={700}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{detail}</Typography>
    </Paper>
  );
}

function BreakdownList({ title, items }: { title: string; items: UtilisationBreakdown[] }) {
  const max = Math.max(1, ...items.map((item) => item.count));
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>{title}</Typography>
      <Stack spacing={1.5}>
        {items.slice(0, 6).map((item, index) => (
          <Box key={item.label}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2">{item.label}</Typography>
              <Typography variant="body2" fontWeight={800}>{formatNumber(item.count)}</Typography>
            </Stack>
            <Box sx={{ mt: 0.75, height: 7, borderRadius: 4, bgcolor: '#e8eff0', overflow: 'hidden' }}>
              <Box sx={{ height: '100%', width: `${(item.count / max) * 100}%`, bgcolor: chartColours[index % chartColours.length], borderRadius: 4 }} />
            </Box>
          </Box>
        ))}
        {items.length > 6 ? (
          <Typography variant="caption" color="text.secondary">+{items.length - 6} more</Typography>
        ) : null}
        {!items.length ? <Typography color="text.secondary">No data available</Typography> : null}
      </Stack>
    </Paper>
  );
}

function CouponPerformance({ items }: { items: UtilisationCouponSummary[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={0.5} sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={800}>Coupon performance</Typography>
        <Typography variant="caption" color="text.secondary">Completed, paid bookings only</Typography>
      </Stack>
      {items.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 680 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr .7fr 1fr', gap: 2, px: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              {['Coupon', 'Discount offered', 'Bookings', 'Discount given'].map((label) => (
                <Typography key={label} variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
              ))}
            </Box>
            {items.map((item) => (
              <Box key={item.code} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr .7fr 1fr', gap: 2, px: 1, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                <Box>
                  <Typography variant="body2" fontWeight={700}>{item.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{item.code}</Typography>
                </Box>
                <Typography variant="body2">{item.offer}</Typography>
                <Typography variant="body2" fontWeight={800}>{formatNumber(item.bookings)}</Typography>
                <Typography variant="body2" fontWeight={800}>{formatCurrency(item.discountAmount)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : <Typography color="text.secondary">No coupon-funded completed bookings.</Typography>}
    </Paper>
  );
}

function BookingRows({ rows }: { rows: UtilisationBooking[] }) {
  const [page, setPage] = React.useState(0);
  return <Paper variant="outlined" sx={{ borderRadius: 3 }}>
    <Typography variant="h6" sx={{ p: 2 }}>Matching bookings ({rows.length})</Typography>
    <TableContainer><Table size="small" aria-label="Matching bookings">
      <TableHead><TableRow>{['Booking / employee', 'Beneficiary', 'Date', 'Status', 'Payment', 'Service / mode', 'Provider / package'].map(label => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
      <TableBody>{rows.slice(page * 25, (page + 1) * 25).map(row => <TableRow key={row.id}>
        <TableCell>{row.name || row.id}<Typography variant="caption" display="block">{row.email || row.phone}</Typography></TableCell>
        <TableCell>{row.dependentId ? 'Dependent' : 'Employee'}</TableCell>
        <TableCell>{row.bookingDate ? `${row.bookingDate.slice(6, 8)}/${row.bookingDate.slice(4, 6)}/${row.bookingDate.slice(0, 4)}` : '—'}</TableCell>
        <TableCell>{row.status?.replace(/_/g, ' ') || 'Unknown'}</TableCell>
        <TableCell>{row.paymentStatus || 'Unknown'}</TableCell>
        <TableCell>{row.service} / {row.bookingMode}</TableCell>
        <TableCell>{row.provider}<Typography variant="caption" display="block">{row.packageName}</Typography></TableCell>
      </TableRow>)}</TableBody>
    </Table></TableContainer>
    {!rows.length ? <Typography sx={{ p: 2 }}>No bookings match these filters. Change the filters and apply again.</Typography> : null}
    <TablePagination component="div" count={rows.length} page={page} onPageChange={(_, next) => setPage(next)} rowsPerPage={25} rowsPerPageOptions={[25]} />
  </Paper>;
}

export default function UtilisationDashboard({ eventId }: Props) {
  const { userData } = useAppContext();
  const isHR = isHRUser(userData);
  const hrCorporateId = getUserCorporateId(userData);
  const [dateFrom, setDateFrom] = React.useState(eventId ? '' : `${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = React.useState(eventId ? '' : inputDate());
  const [service, setService] = React.useState<'all' | 'diagnostics' | 'consultation'>('all');
  const [beneficiary, setBeneficiary] = React.useState<'all' | 'employee' | 'dependent'>('all');
  const [statuses, setStatuses] = React.useState<string[]>([]);
  const [paymentStatus, setPaymentStatus] = React.useState('');
  const [bookingMode, setBookingMode] = React.useState('');
  const statusLabelId = React.useId();
  const [corporates, setCorporates] = React.useState<CorporateOption[]>([]);
  const [corporateId, setCorporateId] = React.useState(isHR ? hrCorporateId : '');
  const [snapshot, setSnapshot] = React.useState<UtilisationDashboardSnapshot | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [appliedQuery, setAppliedQuery] = React.useState<UtilisationDashboardQuery | null>(null);
  const initialised = React.useRef(false);
  const requestSequence = React.useRef(0);

  const draftQuery = React.useMemo<UtilisationDashboardQuery>(() => ({
    scope: eventId ? 'event' : 'corporate',
    ...(eventId ? { eventId } : { corporateId }),
    ...(dateFrom ? { dateFrom: yyyyMmDd(dateFrom) } : {}),
    ...(dateTo ? { dateTo: yyyyMmDd(dateTo) } : {}),
    service,
    beneficiary,
    ...(statuses.length ? { statuses: statuses.join(',') } : {}),
    ...(paymentStatus ? { paymentStatus } : {}),
    ...(bookingMode ? { bookingMode } : {}),
  }), [beneficiary, corporateId, dateFrom, dateTo, eventId, service, statuses, paymentStatus, bookingMode]);

  React.useEffect(() => {
    if (eventId || isHR) return;
    let active = true;
    doGET('/corporates').then((response) => {
      if (!active) return;
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      setCorporates(rows);
      setCorporateId((current: string) => current || rows[0]?._id || '');
    });
    return () => { active = false; };
  }, [eventId, isHR]);

  React.useEffect(() => {
    if (isHR && hrCorporateId) setCorporateId(hrCorporateId);
  }, [hrCorporateId, isHR]);

  const applyFilters = React.useCallback(() => {
    if (!eventId && !corporateId) return;
    if (dateFrom && dateTo && dateFrom > dateTo) {
      setError('From date must be before To date.');
      return;
    }
    setAppliedQuery({ ...draftQuery });
  }, [corporateId, dateFrom, dateTo, draftQuery, eventId]);

  React.useEffect(() => {
    if (initialised.current || (!eventId && !corporateId)) return;
    initialised.current = true;
    setAppliedQuery({ ...draftQuery });
  }, [corporateId, draftQuery, eventId]);

  React.useEffect(() => {
    if (!appliedQuery) return;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError('');
    fetchUtilisationDashboard(appliedQuery)
      .then((result) => {
        if (requestSequence.current === requestId) setSnapshot(result);
      })
      .catch((loadError: any) => {
        if (requestSequence.current !== requestId) return;
        setSnapshot(null);
        setError(loadError.message || 'Failed to load utilisation dashboard');
      })
      .finally(() => {
        if (requestSequence.current === requestId) setLoading(false);
      });
    return () => {
      if (requestSequence.current === requestId) requestSequence.current += 1;
    };
  }, [appliedQuery]);

  const downloadReport = async (format: 'pdf' | 'xlsx') => {
    if (!snapshot || !appliedQuery || loading || downloading) return;
    setDownloading(true);
    setError('');
    try {
      await downloadUtilisationDashboardPdf(appliedQuery, snapshot.scope.name.replace(/\s+/g, '-').toLowerCase(), format);
    } catch (downloadError: any) {
      setError(downloadError.message || 'Failed to download report. Please retry.');
    } finally {
      setDownloading(false);
    }
  };

  const cards = snapshot?.scope.type === 'event'
    ? [
        ['Registered', snapshot.overview.registeredUsers || 0, 'Eligible for this event', '#01999e'],
        ['Booked users', snapshot.overview.bookedUsers || 0, 'Unique employees with bookings', '#3f73d8'],
        ['Attended', snapshot.overview.attendedUsers || 0, 'Completed attendance milestones', '#72bf24'],
        ['Open bookings', snapshot.overview.openBookings, 'Still pending or confirmed', '#d99318'],
      ]
    : snapshot
      ? [
          ['Active employees', snapshot.overview.eligibleUsers, 'Corporate denominator', '#01999e'],
          ['Employees engaged', snapshot.overview.engagedUsers, 'Unique employees with bookings', '#3f73d8'],
          ['Completed services', snapshot.overview.completedServices, 'Diagnostics and consultations', '#72bf24'],
          ['Dependent services', snapshot.overview.dependentServices, 'Completed for dependents', '#7757c8'],
        ]
      : [];

  const trendOption = snapshot ? {
    aria: { enabled: true, description: 'Monthly completed services trend' },
    tooltip: { trigger: 'axis' },
    grid: { left: 42, right: 20, top: 20, bottom: 40 },
    xAxis: {
      type: 'category',
      data: snapshot.trends.map((item) => displayMonth(item.date)),
      axisLine: { lineStyle: { color: '#cddadd' } },
    },
    yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#edf2f3' } } },
    series: [{
      type: 'line',
      smooth: true,
      symbolSize: 8,
      data: snapshot.trends.map((item) => item.count),
      lineStyle: { color: '#01999e', width: 3 },
      itemStyle: { color: '#01999e' },
      areaStyle: { color: 'rgba(1,153,158,0.12)' },
    }],
  } : {};

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ md: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={900} color="#173f46">
            {eventId ? 'Event utilisation' : 'Corporate utilisation'}
          </Typography>
          <Typography color="text.secondary">
            {eventId
              ? 'Registration, booking and attendance visibility for this event.'
              : 'Employee and dependent diagnostics and consultation activity.'}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {(['xlsx', 'pdf'] as const).map(format => <Button key={format} variant="outlined" startIcon={downloading ? <CircularProgress size={16} /> : <DownloadOutlinedIcon />} onClick={() => downloadReport(format)} disabled={!snapshot || !appliedQuery || downloading || loading} sx={{ minHeight: 44 }}>
            Download {format === 'xlsx' ? 'Excel' : 'PDF'}
          </Button>)}
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} useFlexGap flexWrap={{ md: 'wrap' }} alignItems={{ md: 'center' }}>
          {!eventId ? (
            <FormControl size="small" sx={{ minWidth: 260 }} disabled={isHR}>
              <InputLabel>Corporate</InputLabel>
              <Select
                label="Corporate"
                value={corporateId}
                onChange={(event) => {
                  const nextCorporateId = String(event.target.value);
                  setCorporateId(nextCorporateId);
                  setAppliedQuery((current) => ({
                    ...(current || draftQuery),
                    corporateId: nextCorporateId,
                  }));
                }}
              >
                {isHR && corporateId ? <MenuItem value={corporateId}>My corporate</MenuItem> : null}
                {corporates.map((corporate) => (
                  <MenuItem key={corporate._id} value={corporate._id}>
                    {corporate.companyName || corporate.name || corporate._id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          <TextField size="small" type="date" label="From" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <TextField size="small" type="date" label="To" value={dateTo} onChange={(event) => setDateTo(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Service</InputLabel>
            <Select label="Service" value={service} onChange={(event) => setService(event.target.value as typeof service)}>
              <MenuItem value="all">All services</MenuItem>
              <MenuItem value="diagnostics">Diagnostics</MenuItem>
              <MenuItem value="consultation">Consultation</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Beneficiary</InputLabel>
            <Select label="Beneficiary" value={beneficiary} onChange={(event) => setBeneficiary(event.target.value as typeof beneficiary)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="employee">Employee</MenuItem>
              <MenuItem value="dependent">Dependent</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id={statusLabelId} shrink>Booking status</InputLabel>
            <Select multiple displayEmpty notched labelId={statusLabelId} label="Booking status" value={statuses} onChange={event => setStatuses(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)} renderValue={selected => selected.length ? selected.map(value => value.replace(/_/g, ' ')).join(', ') : 'All statuses'}>
              {BOOKING_STATUS_OPTIONS.map(status => <MenuItem key={status} value={status}><Checkbox checked={statuses.includes(status)} tabIndex={-1} inputProps={{ 'aria-hidden': true }} /><ListItemText primary={status.replace(/_/g, ' ')} /></MenuItem>)}
            </Select>
          </FormControl>
          <TextField select size="small" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} label="Payment status" value={paymentStatus} onChange={event => setPaymentStatus(event.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All payments</MenuItem>
            {['paid', 'pending', 'failed', 'cancelled', 'refunded'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <TextField select size="small" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} label="Booking mode" value={bookingMode} onChange={event => setBookingMode(event.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All modes</MenuItem>
            {['Home', 'Centre', 'Hybrid', 'Virtual'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
          </TextField>
          <Button variant="outlined" onClick={() => { setStatuses([]); setPaymentStatus(''); setBookingMode(''); setService('all'); setBeneficiary('all'); setDateFrom(''); setDateTo(''); setAppliedQuery({ scope: eventId ? 'event' : 'corporate', ...(eventId ? { eventId } : { corporateId }), service: 'all', beneficiary: 'all' }); }} disabled={loading || (!eventId && !corporateId)}>Clear filters</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={applyFilters} disabled={loading || (!eventId && !corporateId)} sx={{ minHeight: 44 }}>Apply filters</Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {loading ? <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}><CircularProgress /></Box> : null}

      {!loading && snapshot ? (
        <>
          {snapshot.scope.description ? (
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, bgcolor: '#eaf8f8' }}>
              <Typography variant="overline" color="#017d82" fontWeight={800}>Description</Typography>
              <Typography>{snapshot.scope.description}</Typography>
            </Paper>
          ) : null}

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6">All matching booking activity</Typography>
            <Typography variant="body2" color="text.secondary">Includes pending, unpaid and cancelled bookings. Exports use the applied filters and include all pages.</Typography>
            <Typography variant="caption" color="text.secondary">Applied filters: {describeFilters(snapshot.filters || appliedQuery)}</Typography>
            <BookingReportSummary summary={snapshot.bookingSummary} />
          </Paper>
          <BookingRows key={JSON.stringify(appliedQuery)} rows={snapshot.bookings || []} />

          <Grid container spacing={2}>
            {cards.map(([label, value, detail, colour]) => (
              <Grid item xs={12} sm={6} lg={3} key={String(label)}>
                <MetricCard label={String(label)} value={formatNumber(Number(value))} detail={String(detail)} colour={String(colour)} />
              </Grid>
            ))}
          </Grid>

          <Paper sx={{ p: 3, borderRadius: 3, bgcolor: '#173f46', color: 'white' }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#bfd4d7' }}>Utilisation</Typography>
                <Typography variant="h3" fontWeight={900}>{snapshot.overview.utilisationRate}%</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#bfd4d7' }}>Amount paid</Typography>
                <Typography variant="h5" fontWeight={800}>{formatCurrency(snapshot.payments.amountPaid)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#bfd4d7' }}>Coupon-funded bookings</Typography>
                <Typography variant="h5" fontWeight={800}>{formatNumber(snapshot.payments.couponBookings)}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={6}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: 360 }}>
                <Typography variant="h6" fontWeight={800}>Monthly completed services</Typography>
                {snapshot.trends.length ? <ReactECharts option={trendOption} style={{ height: 290 }} /> : <Typography color="text.secondary" sx={{ mt: 4 }}>No completed services in this period.</Typography>}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: 360 }}>
                <Typography variant="h6" fontWeight={800}>Service mix</Typography>
                {snapshot.serviceMix.length ? <ReactECharts option={breakdownOption(snapshot.serviceMix, 'Service mix')} style={{ height: 290 }} /> : <Typography color="text.secondary" sx={{ mt: 4 }}>No data available.</Typography>}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <BreakdownList title="Booking mode" items={snapshot.bookingMode} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}><BreakdownList title="Booking status" items={snapshot.statuses} /></Grid>
            <Grid item xs={12} md={6} lg={3}><BreakdownList title="Beneficiaries" items={snapshot.beneficiaries} /></Grid>
            <Grid item xs={12} md={6} lg={3}><BreakdownList title="Patient gender" items={snapshot.gender} /></Grid>
            <Grid item xs={12} md={6} lg={3}><BreakdownList title="Funding method" items={snapshot.funding} /></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}><BreakdownList title="Leading providers" items={snapshot.providers} /></Grid>
            <Grid item xs={12} md={6}><BreakdownList title="Leading packages / services" items={snapshot.packages} /></Grid>
          </Grid>

          <CouponPerformance items={snapshot.coupons || []} />

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>How to read this dashboard</Typography>
            <Grid container spacing={2}>
              {Object.entries(snapshot.definitions).map(([label, definition]) => (
                <Grid item xs={12} md={6} key={label}>
                  <Typography variant="body2" fontWeight={800}>{label}</Typography>
                  <Typography variant="caption" color="text.secondary">{definition}</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </>
      ) : null}
    </Stack>
  );
}
